/**
 * Case Infrastructure Tab
 *
 * Displays and manages case endpoints (infrastructure).
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { InfrastructureMapCanvas } from '../../components/infrastructure/InfrastructureMapCanvas'
import { CaseInfrastructureTabHeader } from './case-infrastructure/CaseInfrastructureTabHeader'
import {
  ErrorState,
  ImportDropdownBackdrop,
  LoadingState,
} from './case-infrastructure/case-infrastructure-tab.parts'
import { CaseInfrastructureListSection } from './case-infrastructure/CaseInfrastructureListSection'
import { CaseInfrastructureModalsSection } from './case-infrastructure/CaseInfrastructureModalsSection'
import { useCaseInfrastructureTab } from './case-infrastructure/use-case-infrastructure-tab'

interface CaseInfrastructureTabProps {
  caseId: string
}

export function CaseInfrastructureTab({ caseId }: CaseInfrastructureTabProps): ReactElement {
  const { t } = useTranslation('cases')
  const state = useCaseInfrastructureTab({ caseId, t })
  const { loading, error, refetch, endpoints, relationships, mapSet, modals, columns } = state

  if (loading) return <LoadingState />
  if (error)
    return (
      <ErrorState
        error={error}
        onRetry={() => {
          void refetch()
        }}
      />
    )

  return (
    <div className="space-y-6">
      <CaseInfrastructureTabHeader
        caseId={caseId}
        t={t}
        endpointsCount={endpoints.length}
        relationshipsCount={relationships.length}
        viewMode={modals.viewMode}
        setViewMode={modals.setViewMode}
        refetch={refetch}
        showColumnSettings={columns.showColumnSettings}
        setShowColumnSettings={columns.setShowColumnSettings}
        visibleColumns={columns.visibleColumns}
        toggleColumn={columns.toggleColumn}
        resetColumns={columns.resetColumns}
        columnDefinitions={columns.columnDefinitions}
        showImportDropdown={modals.showImportDropdown}
        setShowImportDropdown={modals.setShowImportDropdown}
        setShowNewImportModal={modals.setShowNewImportModal}
        setShowRelationshipImportModal={modals.setShowRelationshipImportModal}
        setShowCsvImportModal={modals.setShowCsvImportModal}
        setShowJsonImportModal={modals.setShowJsonImportModal}
        setShowCreateModal={modals.setShowCreateModal}
      />
      {modals.viewMode === 'map' && (
        <div className="h-[calc(100vh-220px)] min-h-[500px] overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
          <InfrastructureMapCanvas caseId={caseId} initialStyle="tree" mapSet={mapSet} />
        </div>
      )}
      {modals.viewMode === 'list' && <CaseInfrastructureListSection state={state} t={t} />}
      <CaseInfrastructureModalsSection state={state} />
      {modals.showImportDropdown && (
        <ImportDropdownBackdrop
          onClose={() => {
            modals.setShowImportDropdown(false)
          }}
        />
      )}
    </div>
  )
}
